import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { dashboardApi, fatturazionIApi } from '../api';
import GaugeChart from '../components/GaugeChart';

function KPI({ label, value, sub, color = 'text-brand-600', to }) {
  const content = (
    <div className="card hover:shadow-md transition-shadow">
      <div className="text-sm text-gray-500">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${color}`}>{value}</div>
      {sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
    </div>
  );
  return to ? <Link to={to}>{content}</Link> : content;
}

function fmt(n) {
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);
}

const MESI_IT = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];

function opzioniMesi() {
  const opts = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const valore = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    opts.push({ valore, label: `${MESI_IT[d.getMonth()]} ${d.getFullYear()}` });
  }
  return opts;
}

function opzioniAnni() {
  const anno = new Date().getFullYear();
  return Array.from({ length: 5 }, (_, i) => String(anno - i));
}

function Delta({ curr, prev, isPercent = false }) {
  if (prev === 0 && curr === 0) return null;
  if (prev === 0) return <span className="text-xs text-green-600 ml-1">nuovo</span>;
  const diff = curr - prev;
  const pct = (diff / prev) * 100;
  if (Math.abs(diff) < 0.005) return <span className="text-xs text-gray-400 ml-1">= periodo prec.</span>;
  const up = diff > 0;
  return (
    <span className={`text-xs ml-1 ${up ? 'text-green-600' : 'text-red-500'}`}>
      {up ? '▲' : '▼'} {isPercent ? `${Math.abs(diff).toFixed(1)} pt` : `${Math.abs(pct).toFixed(0)}%`}
    </span>
  );
}

/** Riceve il periodo dalla pagina: non ha più un filtro proprio. */
function OfferteUtentePanel({ periodo, valore }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!valore) return;
    setLoading(true);
    dashboardApi.offertePerUtente({ periodo, valore }).then(setData).catch(console.error).finally(() => setLoading(false));
  }, [periodo, valore]);

  const righe = data?.righe || [];
  const totali = righe.reduce((acc, r) => ({
    offerte_emesse: acc.offerte_emesse + Number(r.offerte_emesse),
    offerte_vinte: acc.offerte_vinte + Number(r.offerte_vinte),
    valore_emesso: acc.valore_emesso + Number(r.valore_emesso),
    valore_vinto: acc.valore_vinto + Number(r.valore_vinto),
    offerte_emesse_prec: acc.offerte_emesse_prec + Number(r.offerte_emesse_prec),
    offerte_vinte_prec: acc.offerte_vinte_prec + Number(r.offerte_vinte_prec),
    valore_emesso_prec: acc.valore_emesso_prec + Number(r.valore_emesso_prec),
    valore_vinto_prec: acc.valore_vinto_prec + Number(r.valore_vinto_prec),
  }), { offerte_emesse: 0, offerte_vinte: 0, valore_emesso: 0, valore_vinto: 0, offerte_emesse_prec: 0, offerte_vinte_prec: 0, valore_emesso_prec: 0, valore_vinto_prec: 0 });

  const pctNumero = (vinte, emesse) => emesse > 0 ? (vinte / emesse) * 100 : 0;
  const pctValore = (vinto, emesso) => emesso > 0 ? (vinto / emesso) * 100 : 0;

  const g = data?.globale;
  const gEmesse = Number(g?.offerte_emesse || 0);
  const gVinte = Number(g?.offerte_vinte || 0);
  const gValEmesso = Number(g?.valore_emesso || 0);
  const gValVinto = Number(g?.valore_vinto || 0);
  const gPctN = gEmesse > 0 ? (gVinte / gEmesse) * 100 : 0;
  const gPctV = gValEmesso > 0 ? (gValVinto / gValEmesso) * 100 : 0;
  const gPctNprec = Number(g?.offerte_emesse_prec || 0) > 0 ? (Number(g.offerte_vinte_prec) / Number(g.offerte_emesse_prec)) * 100 : 0;
  const gPctVprec = Number(g?.valore_emesso_prec || 0) > 0 ? (Number(g.valore_vinto_prec) / Number(g.valore_emesso_prec)) * 100 : 0;

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h2 className="text-sm font-semibold text-gray-700">
          Andamento offerte {data?.label ? <span className="font-normal text-gray-400">· {data.label}</span> : null}
        </h2>
      </div>

      {/* Riepilogo globale del periodo — calcolato su tutte le offerte */}
      {!loading && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="text-xs text-gray-500">Offerte emesse</div>
            <div className="text-xl font-bold text-gray-800 mt-0.5">
              {gEmesse}
              <Delta curr={gEmesse} prev={Number(g?.offerte_emesse_prec || 0)} />
            </div>
            <div className="text-xs text-gray-400 mt-0.5">{fmt(gValEmesso)}</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="text-xs text-gray-500">Offerte acquisite</div>
            <div className="text-xl font-bold text-green-600 mt-0.5">
              {gVinte}
              <Delta curr={gVinte} prev={Number(g?.offerte_vinte_prec || 0)} />
            </div>
            <div className="text-xs text-gray-400 mt-0.5">{fmt(gValVinto)}</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="text-xs text-gray-500">% offerte acquisite</div>
            <div className="text-xl font-bold text-brand-600 mt-0.5">
              {gPctN.toFixed(1)}%
              <Delta curr={gPctN} prev={gPctNprec} isPercent />
            </div>
            <div className="text-xs text-gray-400 mt-0.5">{gVinte} su {gEmesse}</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="text-xs text-gray-500">% importo acquisito</div>
            <div className="text-xl font-bold text-purple-600 mt-0.5">
              {gPctV.toFixed(1)}%
              <Delta curr={gPctV} prev={gPctVprec} isPercent />
            </div>
            <div className="text-xs text-gray-400 mt-0.5">{fmt(gValVinto)} su {fmt(gValEmesso)}</div>
          </div>
        </div>
      )}

      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Dettaglio per utente</h3>

      {loading ? (
        <div className="text-center text-gray-400 text-sm py-8">Caricamento…</div>
      ) : righe.length === 0 ? (
        <div className="text-center text-gray-400 text-sm py-6 bg-gray-50 rounded-lg">
          Nessuna offerta con utente assegnato in questo periodo.<br />
          <span className="text-xs">L'utente si ricava dalle iniziali del numero offerta: se mancano, verifica che l'utente sia configurato in Utenti e Accessi.</span>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 border-b">
                <th className="pb-2">Utente</th>
                <th className="pb-2 text-right">Emesse</th>
                <th className="pb-2 text-right">Vinte</th>
                <th className="pb-2 text-right">% successo (n°)</th>
                <th className="pb-2 text-right">Valore emesso</th>
                <th className="pb-2 text-right">Valore vinto</th>
                <th className="pb-2 text-right">% successo (valore)</th>
              </tr>
            </thead>
            <tbody>
              {righe.map(r => {
                const pN = pctNumero(r.offerte_vinte, r.offerte_emesse);
                const pNprec = pctNumero(r.offerte_vinte_prec, r.offerte_emesse_prec);
                const pV = pctValore(r.valore_vinto, r.valore_emesso);
                const pVprec = pctValore(r.valore_vinto_prec, r.valore_emesso_prec);
                return (
                  <tr key={r.id} className="border-b last:border-0 hover:bg-gray-50">
                    <td className="py-2 font-medium text-gray-800">{r.nome_completo}</td>
                    <td className="py-2 text-right">
                      {r.offerte_emesse}
                      <Delta curr={Number(r.offerte_emesse)} prev={Number(r.offerte_emesse_prec)} />
                    </td>
                    <td className="py-2 text-right">
                      {r.offerte_vinte}
                      <Delta curr={Number(r.offerte_vinte)} prev={Number(r.offerte_vinte_prec)} />
                    </td>
                    <td className="py-2 text-right">
                      {pN.toFixed(0)}%
                      <Delta curr={pN} prev={pNprec} isPercent />
                    </td>
                    <td className="py-2 text-right">
                      {fmt(r.valore_emesso)}
                      <Delta curr={Number(r.valore_emesso)} prev={Number(r.valore_emesso_prec)} />
                    </td>
                    <td className="py-2 text-right">
                      {fmt(r.valore_vinto)}
                      <Delta curr={Number(r.valore_vinto)} prev={Number(r.valore_vinto_prec)} />
                    </td>
                    <td className="py-2 text-right">
                      {pV.toFixed(0)}%
                      <Delta curr={pV} prev={pVprec} isPercent />
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t-2 font-semibold text-gray-800">
                <td className="pt-2">Totale</td>
                <td className="pt-2 text-right">{totali.offerte_emesse}</td>
                <td className="pt-2 text-right">{totali.offerte_vinte}</td>
                <td className="pt-2 text-right">{pctNumero(totali.offerte_vinte, totali.offerte_emesse).toFixed(0)}%</td>
                <td className="pt-2 text-right">{fmt(totali.valore_emesso)}</td>
                <td className="pt-2 text-right">{fmt(totali.valore_vinto)}</td>
                <td className="pt-2 text-right">{pctValore(totali.valore_vinto, totali.valore_emesso).toFixed(0)}%</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
      <p className="text-xs text-gray-400 mt-3">Confronto rispetto {data?.periodo === 'year' ? "all'anno" : 'al mese'} precedente ({data?.prevLabel}).</p>
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Cosa è fermo: non dipende dal periodo scelto — un residuo del 2025 è in
  // ritardo qualunque mese si stia guardando.
  const [ritardo, setRitardo] = useState(null);
  // Periodo di riferimento dell'INTERA pagina
  const [periodo, setPeriodo] = useState('year');
  const [valore, setValore] = useState(String(new Date().getFullYear()));

  // Periodo e valore cambiano INSIEME: aggiornarli in due effetti separati
  // farebbe partire una richiesta con il valore ancora riferito all'altra vista.
  const cambiaPeriodo = (nuovo) => {
    setPeriodo(nuovo);
    setValore(nuovo === 'month' ? opzioniMesi()[0].valore : opzioniAnni()[0]);
  };

  useEffect(() => { fatturazionIApi.inRitardo().then(setRitardo).catch(() => setRitardo(null)); }, []);

  useEffect(() => {
    if (!valore) return;
    const coerente = periodo === 'year' ? /^\d{4}$/.test(valore) : /^\d{4}-\d{2}$/.test(valore);
    if (!coerente) return;
    let annullata = false;
    setLoading(true);
    dashboardApi.get({ periodo, valore })
      .then(d => { if (!annullata) setData(d); })
      .catch(console.error)
      .finally(() => { if (!annullata) setLoading(false); });
    return () => { annullata = true; };
  }, [periodo, valore]);

  const opzioniPeriodo = periodo === 'month'
    ? opzioniMesi()
    : opzioniAnni().map(a => ({ valore: a, label: a }));
  // Etichetta leggibile ("Ago 2026" invece di "2026-08")
  const etichettaPeriodo = opzioniPeriodo.find(o => o.valore === valore)?.label || valore;

  // Barra di selezione: resta montata anche durante il caricamento,
  // altrimenti il filtro sparirebbe a ogni cambio di periodo
  const barraPeriodo = (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="page-title">Dashboard</h1>
        <div className="flex rounded-lg border border-gray-300 overflow-hidden text-xs shrink-0">
          {[['month', 'Mensile'], ['year', 'Annuale']].map(([v, l]) => (
            <button key={v} type="button"
              className={`px-4 py-1.5 font-medium ${periodo === v ? 'bg-brand-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              onClick={() => cambiaPeriodo(v)}>{l}</button>
          ))}
        </div>
      </div>
      <div className="flex gap-1.5 flex-wrap">
        {opzioniPeriodo.map(o => (
          <button key={o.valore} type="button" onClick={() => setValore(o.valore)}
            className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
              valore === o.valore
                ? 'bg-brand-600 border-brand-600 text-white'
                : 'bg-white border-gray-200 text-gray-500 hover:border-brand-300 hover:text-brand-600'
            }`}>{o.label}</button>
        ))}
      </div>
      <p className="text-xs text-gray-400">
        Tutti i dati della pagina si riferiscono al periodo selezionato.
      </p>
    </div>
  );

  if (loading) return (
    <div className="space-y-6">
      {barraPeriodo}
      <div className="flex items-center justify-center h-64 text-gray-400">Caricamento…</div>
    </div>
  );
  if (!data) return (
    <div className="space-y-6">
      {barraPeriodo}
      <div className="text-red-500">Errore nel caricamento dashboard. Verificare connessione al server.</div>
    </div>
  );

  const { commesse, offerte, fatturazioni: f, costi, offerte_consulenti: oc, clienti, margine, acquisizione: acq, fatturato_mesi, scadenze } = data;

  const nz = v => (v === null || v === undefined ? null : parseFloat(v));

  const margPct = margine?.margine_pct === null || margine?.margine_pct === undefined ? null : parseFloat(margine.margine_pct);

  const chartData = fatturato_mesi.map(m => ({
    mese: m.mese?.slice(5) || m.mese,
    importo: parseFloat(m.importo)
  }));

  return (
    <div className="space-y-6">
      {barraPeriodo}

      {/* Indicatori chiave — misuratori radiali */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <GaugeChart
          titolo="Marginalità commesse"
          sottotitolo={`Budget al netto dei costi consulenti · ${etichettaPeriodo}`}
          valore={margPct}
          soglie={[50, 25]}
          dettaglio={margPct === null
            ? 'Nessun costo consulente imputato nel periodo'
            : `${fmt(Number(margine.budget_con_costi) - Number(margine.costo_con_costi))} su ${fmt(margine.budget_con_costi)} · ${margine.n_con_costi} commesse`}
        />
        <GaugeChart
          titolo="Offerte acquisite — numero"
          sottotitolo={etichettaPeriodo}
          valore={nz(acq?.pct_numero)}
          soglie={[60, 40]}
          dettaglio={acq ? `${acq.acquisite} acquisite su ${acq.emesse} emesse` : undefined}
        />
        <GaugeChart
          titolo="Offerte acquisite — valore"
          sottotitolo={etichettaPeriodo}
          valore={nz(acq?.pct_valore)}
          soglie={[60, 40]}
          dettaglio={acq ? `${fmt(acq.valore_acquisito)} su ${fmt(acq.valore_emesso)}` : undefined}
        />
      </div>

      {/* KPI principali */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI label="Commesse avviate" value={commesse.attive} sub={`Budget: ${fmt(commesse.budget_attivo)}`} to="/commesse" />
        <KPI label="Fatturato incassato" value={fmt(f.incassato)} color="text-green-600" to="/fatturazioni" />
        <KPI
          label="Da emettere"
          value={fmt(f.da_emettere)}
          color="text-yellow-600"
          to="/fatturazioni"
          sub={Math.abs(Number(f.da_emettere_totale) - Number(f.da_emettere)) > 0.5
            ? `su ${fmt(f.da_emettere_totale)} totali aperti`
            : 'tutto il residuo aperto'}
        />
        {/* Un numero che non si può aprire non serve a decidere: questo porta
            all'elenco delle commesse ferme, con la chiusura a un clic. */}
        <KPI label="In ritardo" value={fmt(ritardo?.totale)} color="text-red-600"
             to="/fatturazioni?vista=ritardo"
             sub={ritardo ? (ritardo.righe.length
                   ? `${ritardo.righe.length} commesse · ${fmt(ritardo.anni_precedenti)} di anni precedenti`
                   : 'niente fermo') : '—'} />
        <KPI label="Pipeline offerte" value={fmt(offerte.pipeline)} sub={`${offerte.inviate} inviate`} color="text-purple-600" to="/offerte" />
        <KPI label="Costi consulenti" value={fmt(costi.totale)} color="text-red-600" to="/costi-consulenti" />
        <KPI label="Budget consulenti" value={fmt(oc.budget_consulenti)} sub={`Imputato: ${fmt(oc.imputato_consulenti)}`} to="/offerte-consulenti" />
        <KPI label="Clienti attivi" value={clienti.totale} color="text-gray-700" to="/clienti" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Grafico fatturato */}
        <div className="card">
          <h2 className="text-sm font-semibold text-gray-700 mb-4">
            {data.periodo === 'year' ? `Fatturato mensile ${etichettaPeriodo}` : `Fatturato — 6 mesi fino a ${etichettaPeriodo}`}
          </h2>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="mese" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                <Tooltip formatter={v => fmt(v)} />
                <Bar dataKey="importo" fill="#2563eb" radius={[4,4,0,0]} name="Fatturato" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-48 text-gray-400 text-sm">Nessun dato disponibile</div>
          )}
        </div>

        {/* Stato offerte */}
        <div className="card">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">Stato offerte</h2>
          <div className="flex gap-6 flex-wrap">
            {[
              { label: 'Bozze', val: offerte.bozze, cls: 'badge-gray' },
              { label: 'Inviate', val: offerte.inviate, cls: 'badge-blue' },
              { label: 'Vinte', val: offerte.vinte, cls: 'badge-green' },
              { label: 'Perse', val: offerte.perse, cls: 'badge-red' },
            ].map(s => (
              <div key={s.label} className="flex items-center gap-2">
                <span className={s.cls}>{s.label}</span>
                <span className="font-semibold text-gray-900">{s.val}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Offerte per utente */}
      <OfferteUtentePanel periodo={periodo} valore={valore} />

      {/* Scadenze imminenti */}
      {scadenze.length > 0 && (
        <div className="card">
          <h2 className="text-sm font-semibold text-gray-700 mb-4">Scadenze imminenti (30 giorni)</h2>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 border-b">
                <th className="pb-2">Commessa</th>
                <th className="pb-2">Descrizione</th>
                <th className="pb-2">Importo</th>
                <th className="pb-2">Scadenza</th>
                <th className="pb-2">Stato</th>
              </tr>
            </thead>
            <tbody>
              {scadenze.map(s => (
                <tr key={s.id} className="border-b last:border-0 hover:bg-gray-50">
                  <td className="py-2 text-gray-600">{s.commessa_numero}</td>
                  <td className="py-2 text-gray-800">{s.descrizione}</td>
                  <td className="py-2 font-medium">{fmt(s.importo)}</td>
                  <td className="py-2 text-red-600">{s.data_scadenza ? new Date(s.data_scadenza).toLocaleDateString('it-IT') : '—'}</td>
                  <td className="py-2">
                    <span className={s.stato === 'emessa' ? 'badge-blue' : 'badge-gray'}>{s.stato === 'emessa' ? 'Emessa' : 'Da emettere'}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
